/**
 * shared/components/PropertyNotesSection.jsx (v2.71.67)
 * -----------------------------------------------------
 * Universal notes/details section used across every dashboard
 * (buyer, dealer, builder, admin, team). The previous
 * implementation buried multi-paragraph notes inside a
 * fixed-height Textarea with `overflow-y: scroll` — unreadable
 * on mobile and broke long-form descriptions.
 *
 * Behaviour:
 *   • Renders notes as natural-flow text (white-space: pre-wrap,
 *     line breaks preserved, line-height 1.7).
 *   • No internal scrolling. No fixed height. No clipping. The
 *     PAGE handles scrolling, never the box.
 *   • Hidden entirely when notes are empty (no orphan label).
 *   • Admin can edit inline via an "Edit" affordance that
 *     swaps to a Textarea on demand. Save calls the parent's
 *     onSave handler.
 *
 * Props:
 *   • notes    (string)
 *   • editable (boolean) — show Edit button + save flow
 *   • onSave   (async fn) — called with the next notes string
 *   • saving   (boolean)
 *   • title    (string, default "Notes")
 */

import React, { useState, useEffect } from "react";
import { Pencil, Check, X as XIcon } from "lucide-react";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";

export default function PropertyNotesSection({
  notes = "",
  editable = false,
  onSave,
  saving = false,
  title = "Notes",
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(notes || "");

  useEffect(() => { setDraft(notes || ""); }, [notes]);

  // Hide section entirely when empty AND viewer cannot edit.
  if (!notes && !editable) return null;
  // Empty + editable: render an "Add notes" stub (Edit btn handles entry too).
  const showEmptyState = !notes;

  const onCancel = () => { setDraft(notes || ""); setEditing(false); };
  const onConfirm = async () => {
    if (!onSave) return;
    await onSave(draft);
    setEditing(false);
  };

  return (
    <section
      className="w-full bg-white border border-slate-200 rounded-2xl p-4 sm:p-5"
      data-testid="property-notes-section"
    >
      <div className="flex items-center justify-between mb-2.5">
        <h3 className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-slate-500">
          {title}
        </h3>
        {editable && !editing && !showEmptyState && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-slate-500 hover:text-[#0055FF] transition-colors"
            data-testid="property-notes-edit-btn"
          >
            <Pencil size={11} /> Edit
          </button>
        )}
      </div>

      {editing ? (
        <div className="space-y-2" data-testid="property-notes-edit-mode">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={Math.min(Math.max(4, (draft || "").split("\n").length + 1), 16)}
            className="rounded-xl text-[13.5px] leading-[1.7]"
            placeholder="Add notes about this property…"
            data-testid="property-notes-textarea"
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={onConfirm}
              disabled={saving}
              className="btn-brand rounded-xl h-9 px-4"
              data-testid="property-notes-save-btn"
            >
              <Check size={13} className="mr-1.5" />
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
              disabled={saving}
              className="rounded-xl h-9 px-4"
              data-testid="property-notes-cancel-btn"
            >
              <XIcon size={13} className="mr-1.5" /> Cancel
            </Button>
          </div>
        </div>
      ) : (notes ? (
        <p
          className="text-[13.5px] sm:text-sm text-[#27272A]"
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            lineHeight: 1.7,
          }}
          data-testid="property-notes-body"
        >
          {notes}
        </p>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="block w-full text-left text-[12.5px] text-slate-400 italic hover:text-[#0055FF] transition-colors"
          data-testid="property-notes-empty-cta"
        >
          No notes yet. Click here to add internal notes…
        </button>
      ))}
    </section>
  );
}
